import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { useMemo, useState } from "react";
import styles from "./index.module.scss";
import Picture from "./picture";
import Files from "/components/pages/product/files";
import { formatType } from "../../../../utils";
import Sert from "../../product/sert";
import Buttons from "./buttons";
import Qty from "./qty";
import Price from "./price";
import Props from "./props";
import Install from "./install";
import { showPopup } from "/redux/action-creaters/popup";
import { popupTypes } from "/redux/reducers/popupReducer";
import { ECOMMERCE_LISTS, useEcommerce } from "../../../../utils/tracking";

const isFarkopOrElectric = (product) =>
  String(product?.IBLOCK_SECTION_ID) === "635" ||
  String(product?.IBLOCK_SECTION_ID) === "636";

const isFarkopOnly = (product) => String(product?.IBLOCK_SECTION_ID) === "635";

export default function Product({
  data,
  viewType,
  managerDelivery,
  managerDeliveryTk,
  managerComment,
  managerInstall,
}) {
  const dispatch = useDispatch();
  const { cityName } = useSelector((state) => state.location);
  const { trackClick } = useEcommerce();

  const images = data.PHOTOS_NEW?.map((pictureId) => {
    const path1 = pictureId.substr(0, 2);
    const path2 = pictureId.substr(2, 2);
    const path3 = pictureId.substr(4);
    return `/images/${path1}/${path2}/${path3}/small.webp`;
  });

  const [isShowDelivery, setShowDelivery] = useState(false);
  const { host } = useSelector((state) => state.params);
  const subDomain = useMemo(() => (host ? host.split(".")[0] : ""), [host]);
  const isCityWithInstall = useMemo(
    () =>
      ["msk", "spb", "nn", "kirov", "ulyanovsk", "petrozavodsk"].includes(
        subDomain,
      ),
    [subDomain],
  );

  const showLifetimeWarranty = useMemo(() => {
    return (
      ["msk", "spb", "nn", "kirov", "ulyanovsk", "petrozavodsk"].includes(
        subDomain,
      ) &&
      Boolean(data?.CATALOG?.AVAILABLE) &&
      isFarkopOnly(data)
    );
  }, [subDomain, data]);

  return (
    <div
      className={styles.Wrapper}
      itemScope
      itemType="http://schema.org/Product"
      onClick={() => trackClick(data, ECOMMERCE_LISTS.catalog)}
    >
      <div
        className={clsx(styles.Product, viewType === 1 && styles.ProductTable)}
      >
        <div className={styles.Left}>
          <div className={styles.Photos}>
            {data.DETAIL_PAGE_URL && (
              <Picture
                stick={data.STICK}
                lifetimeWarranty={showLifetimeWarranty}
                name={data.NAME}
                alt={`${data.NAME} ${data.ARTICLE?.VALUE} в ${cityName}`}
                link={data.DETAIL_PAGE_URL ?? "/"}
                video={data.VIDEO}
                path={data.DETAIL_PAGE_URL}
                images={images}
              />
            )}
            <div className={styles.Install}>
              <Install data={data} />
            </div>
            {data?.CATALOG?.AVAILABLE && isFarkopOrElectric(data) && (
              <span
                className={styles.CalculatorUnderPhoto}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    showPopup(popupTypes.installCalculator, { product: data }),
                  );
                }}
              >
                <span className={styles.CalculatorIcon}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="4" y="2" width="16" height="20" rx="2.5" />
                    <path d="M6 6h12M6 9h12" />
                    <circle cx="7.5" cy="13.5" r="1.25" />
                    <circle cx="12" cy="13.5" r="1.25" />
                    <circle cx="16.5" cy="13.5" r="1.25" />
                    <circle cx="7.5" cy="17" r="1.25" />
                    <circle cx="12" cy="17" r="1.25" />
                    <circle cx="16.5" cy="17" r="1.25" />
                  </svg>
                </span>
                {isCityWithInstall
                  ? "Рассчитать стоимость установки"
                  : `Рассчитать стоимость с ${data?.IBLOCK_SECTION_ID === 635 ? "электрикой" : "фаркопом"}`}
              </span>
            )}
          </div>

          <div className={styles.Files}>
            <Files file={data.FILE} />
            <Sert sert={data.SERT} />
          </div>
        </div>
        <div className={styles.Right}>
          <div className={styles.Top}>
            <div className={styles.Art}>Артикул {data.ARTICLE?.VALUE}</div>
            <Qty product={data} />
          </div>
          <Link href={data.DETAIL_PAGE_URL ?? "/"}>
            <a className={styles.Name} itemProp="url">
              <span itemProp="name">{formatType(data.NAME)}</span>
            </a>
          </Link>
          <span style={{ display: "none" }} itemProp="description">
            {data.NAME}
          </span>

          {typeof managerInstall === "object" &&
            Object.keys(managerInstall).length > 0 && (
              <div className={styles.ManagerInfo}>
                {Object.keys(managerInstall).map((itemKey) => (
                  <div key={itemKey}>
                    <div>
                      {itemKey} - {managerInstall[itemKey]} мин
                    </div>
                  </div>
                ))}
              </div>
            )}

          {Boolean(managerComment) && (
            <span
              className={styles.ManagerInfo}
              style={{ color: "red" }}
              dangerouslySetInnerHTML={{ __html: managerComment }}
            ></span>
          )}

          <Price product={data} />
          <div className={styles.InstallMobile}>
            <Install data={data} />
          </div>
          <div className={styles.Bottom}>
            <div>
              <Props
                props={data.PROPERTIES}
                filterUrl={data.FILTER_URL}
                isBaltex={false}
              />
            </div>
            <Buttons product={data} />
          </div>
        </div>
      </div>
      {(Boolean(managerDelivery) || Boolean(managerDeliveryTk)) && (
        <div className={styles.ManagerWrapper}>
          <a
            href={`https://admin.farkop.ru/bitrix/admin/iblock_element_edit.php?IBLOCK_ID=17&type=1c_catalog&lang=ru&ID=${data.ID}&find_section_section=-1&WF=Y`}
            target="_blank"
            rel="noreferrer"
          >
            Ссылка на товар в Bitrix
          </a>
          {Boolean(managerDelivery) && (
            <div>
              Доставка в магазины:
              <div
                className={styles.ManagerData}
                dangerouslySetInnerHTML={{ __html: managerDelivery }}
              />
            </div>
          )}
          {Boolean(managerDeliveryTk) && (
            <div>
              <span
                className={styles.ManagerDeliveryButton}
                onClick={() => {
                  setShowDelivery(!isShowDelivery);
                }}
              >
                Доставка в регион
              </span>
              {isShowDelivery && (
                <div
                  className={clsx(styles.ManagerData, styles.ManagerDataTK)}
                  dangerouslySetInnerHTML={{ __html: managerDeliveryTk }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
